import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, FlaskConical, Loader2 } from 'lucide-react';
import { ValidationBadge } from './ValidationBadge';
import { CheckBox } from './ChecklistBar';
import { MapChip, MonsterChip } from './LinkChip';
import { QuestDropSummary } from './QuestDropSummary';
import { itemPath } from '../../utils/worldFormat';
import { ItemThumb } from '../world/ItemSprite';
import { recipePath } from '../../data/guide/recipes';
import type { Checklist } from '../../hooks/useChecklist';
import type { WorldLookup } from '../../utils/progressionLookup';
import type { Material } from '../../types/progression';
type Props = {
  materials: Material[];
  list: Checklist;
  prefix: string;
  lookup?: WorldLookup;
  status: string;
  /** Receta actual, para el enlace de regreso desde la receta anterior. */
  fromRecipe?: string;
};
const btn = 'inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-xs font-semibold whitespace-nowrap transition-colors duration-150';

/**
 * Ingredientes o materiales. Valida por separado identidad (catálogo), obtención (rutas)
 * y cantidad (receta o misión). Nada de esto confirma la receta completa.
 */
export function MaterialList({
  materials,
  list,
  prefix,
  lookup,
  status,
  fromRecipe
}: Props) {
  const loading = status === 'loading' || status === 'idle';
  return <ul className="grid gap-2" data-id="element-1093" data-mp="3ud97uz2giq5e">
      {materials.map(m => {
      const key = `${prefix}:${m.id}`;
      const have = list.isChecked(key);
      const isItem = !!(m.gameName || m.itemId);
      const ident = isItem ? lookup?.identify(m) : undefined;
      const routes = ident ? lookup!.routes(ident.id) : undefined;
      const activeRoutes = routes ? routes.total - routes.disabled : 0;
      // Siempre el nombre del juego; el español solo para materiales genéricos.
      const title = ident?.name ?? m.gameName ?? m.esName ?? 'Material';
      return <li key={m.id} className={`rounded-2xl border p-3.5 transition-colors duration-150 ${have ? 'border-[#D4AF37]/40 bg-[#D4AF37]/[0.05]' : 'border-white/10 bg-[#0A1628]'}`} data-id="element-1094" data-mp="19ttkqu878twz">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between" data-id="element-1095" data-mp="383akxwt0p09b">
              <div className="flex min-w-0 gap-3" data-id="element-1096" data-mp="2ucduabinit4w">
              <ItemThumb id={ident?.id} name={title} data-id="element-1097" data-mp="2ubm9lvqlefte" />
              <div className="min-w-0" data-id="element-1098" data-mp="2zap5apkon9op">
                <p className="flex flex-wrap items-baseline gap-x-2" data-id="element-1099" data-mp="2z9nvspjbev93">
                  <span className="font-semibold text-white" data-id="element-1100" data-mp="2kdomjbkgyp2d">{title}</span>
                  <span className="text-sm text-gray-300" data-id="element-1101" data-mp="2pcsz8c6lr4xg">× {m.qty ?? '—'}</span>
                </p>
                {m.requirement && <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-300" data-id="element-1102" data-mp="2bel4max1gfpg">
                    {m.requirement}
                    {m.requirementStatus && <ValidationBadge status={m.requirementStatus} data-id="element-1103" data-mp="9m6e4ngfi2xf" />}
                  </p>}
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1104" data-mp="ekq4r15yw2w4">
                  {m.qty ? <ValidationBadge status={m.qtyStatus} label={m.qtyStatus === 'clasica' ? 'Cantidad: referencia clásica' : undefined} data-id="element-1105" data-mp="ooomesd84vpd" /> : <ValidationBadge status="pendiente" label="Cantidad pendiente" data-id="element-1106" data-mp="jrodm9ozpeuk" />}
                  {isItem && !loading && (ident ? <ValidationBadge status="identificado" data-id="element-1107" data-mp="sqs07nnd8cdt" /> : <ValidationBadge status="pendiente" label="No identificado en el catálogo" data-id="element-1108" data-mp="soyse510nm48" />)}
                  {ident && !routes!.quest.length && (activeRoutes > 0 ? <ValidationBadge status="documentado" label={`${activeRoutes} ${activeRoutes === 1 ? 'ruta' : 'rutas'} de obtención`} data-id="element-1109" data-mp="12glb0mfoonh0" /> : <ValidationBadge status="pendiente" label="Ruta de obtención pendiente" data-id="element-1110" data-mp="1ggdzsc7ngtmc" />)}
                </div>
                {ident && ident.variants.length > 0 && <p className="mt-1 text-xs text-gray-500" data-id="element-1111" data-mp="3i77jnboo7x05">
                    {ident.variants.length} {ident.variants.length === 1 ? 'variante' : 'variantes'} en el catálogo: {ident.variants.map(v => v.name).join(', ')}
                  </p>}
                {m.note && <p className="mt-1.5 text-xs text-gray-400" data-id="element-1112" data-mp="3fp5h9fahgtgp">{m.note}</p>}
                {routes && routes.quest.length > 0 && <QuestDropSummary quest={routes.quest} data-id="element-1113" data-mp="3knytz5hy4gft" />}
                {m.classic && !(routes && routes.quest.length) && <div className="mt-2" data-id="element-1114" data-mp="3tnc502lpxd2n">
                    <p className="text-[11px] text-sky-200/80" data-id="element-1115" data-mp="3tm154dve8pgt">Asociado en la referencia clásica (no es un drop confirmado):</p>
                    <div className="mt-1 flex flex-wrap gap-1.5" data-id="element-1116" data-mp="30y4yagc41h6b">
                      {m.classic.monster && <MonsterChip name={m.classic.monster} lookup={lookup} data-id="element-1117" data-mp="30vsj2axqlkwy" />}
                      {m.classic.map && <MapChip name={m.classic.map} lookup={lookup} data-id="element-1118" data-mp="35v57v1865226" />}
                    </div>
                  </div>}
              </div>
              </div>
              <div className="shrink-0" data-id="element-1119" data-mp="2d816d68ab5v5">
                <CheckBox checked={have} onChange={() => list.toggle(key)} label="Ya lo tengo" data-id="element-1120" data-mp="2i7nhna4jyc1b" />
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-1121" data-mp="2an36tnq2phob">
              {m.recipeId && <Link to={`${recipePath(m.recipeId)}${fromRecipe ? `?desde=${fromRecipe}` : ''}`} className={`${btn} bg-[#D4AF37] text-[#050b14] hover:bg-[#F3E5AB]`} data-id="element-1122" data-mp="25oryxijc9h8q">
                  <FlaskConical className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1123" data-mp="1wod8y5vfjdyw" />
                  Ver cómo crearlo
                </Link>}
              {isItem && (ident ? <Link to={`${itemPath(ident.id)}${fromRecipe ? `?desde=${fromRecipe}` : ''}#donde-conseguirlo`} className={`${btn} border border-white/15 text-gray-200 hover:border-[#D4AF37]/50 hover:text-white`} data-id="element-1124" data-mp="1mytqv2fjjo53">
                    <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1125" data-mp="1d0w0o9e4fh9u" />
                    Dónde conseguirlo
                  </Link> : loading ? <span className={`${btn} border border-white/10 text-gray-500`} data-id="element-1126" data-mp="13zxx1s7lhbrs">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" data-id="element-1127" data-mp="14221cb9eocsl" />
                    Cargando datos…
                  </span> : <span className={`${btn} border border-white/10 text-gray-500`} data-id="element-1128" data-mp="yf8haqfqsgs1">Ubicación de obtención pendiente de confirmar</span>)}
            </div>
          </li>;
    })}
    </ul>;
}